import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import { getSaleProduct, updateSaleProduct } from "../api/saleProductsApi";
import { mapDetailToInitialValues } from "../utils/saleProductForm.utils";
import SaleProductFormPage from "./SaleProductFormPage";

const LIST_PATH = "/stock/base-de-datos/productos-de-venta";

function errorToMessage(error) {
  const message = error?.data?.message || error?.message;
  return Array.isArray(message) ? message.join(" | ") : message || "No se pudo cargar el producto.";
}

export default function EditSaleProductPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { saleProductId } = useParams();
  const numericId = Number(saleProductId);
  const reviewMode = Boolean(location.state?.reviewMode);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError("");
    try {
      const result = await getSaleProduct(numericId, { signal });
      if (!signal.aborted) setProduct(result);
    } catch (requestError) {
      if (!signal.aborted) setError(errorToMessage(requestError));
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [numericId]);

  useEffect(() => {
    if (!Number.isSafeInteger(numericId) || numericId <= 0) return undefined;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, numericId, retry]);

  if (!Number.isSafeInteger(numericId) || numericId <= 0) {
    return <Navigate to={LIST_PATH} replace />;
  }
  if (loading) {
    return (
      <Stack sx={{ minHeight: 360, alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={30} />
        <Typography sx={{ mt: 1.5, fontWeight: 700 }}>Cargando producto...</Typography>
      </Stack>
    );
  }
  if (error || !product) {
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <StockPageHeader title="Editar producto" onBack={() => navigate(LIST_PATH)} />
        <Box sx={{ p: 3, borderRadius: "22px", bgcolor: "neutral.300" }}>
          <Alert severity="error">{error || "No se pudo cargar el producto."}</Alert>
          <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: "flex-end" }}>
            <Button onClick={() => navigate(LIST_PATH)}>Volver</Button>
            <Button variant="contained" onClick={() => setRetry((value) => value + 1)}>Reintentar</Button>
          </Stack>
        </Box>
      </Stack>
    );
  }

  return (
    <SaleProductFormPage
      mode="edit"
      reviewMode={reviewMode}
      pageTitle={reviewMode ? "Revisar producto" : "Editar producto"}
      initialValues={mapDetailToInitialValues(product)}
      onSave={(payload) => updateSaleProduct(numericId, payload)}
    />
  );
}
